// A visual's OWN filtering capability.
//
// Every visual can be handed a filter EXPRESSION and filters itself: the host
// passes the expression down (`<InsightVisual filter={expr}>`), the visual
// evaluates it against the data it has already loaded and renders the surviving
// rows. No host pre-processes a dataset, no surface re-queries, and every
// renderer — vendored chart or native (table / cards / pivot / feeds / map / …) —
// inherits the behaviour from the one host, so a filter means the same thing
// everywhere.
//
// The expression shape deliberately mirrors the authored CRITERIA groups the SQL
// side already uses (`{condition: 'and'|'or', rules: [...]}` with leaf rules
// carrying `column` / `operator` / `values`), so a designed criterion and an
// ad-hoc quick filter are the same idea evaluated in two places.
//
//   {
//     condition: 'and',
//     rules: [
//       { column: 'REGION',  operator: 'in', values: ['East', 'West'] },
//       { column: 'REVENUE', operator: '>',  values: [1000] },
//       { condition: 'or', rules: [ … ] },          // nested group
//     ],
//   }
//
// Consumers: the dashboard quick-filter mode (quickFilter.ts builds an
// expression from its chips) and anything else that needs a visual to narrow
// itself client-side.

import { mapVisualData, pickGroup } from './dataUtils'
import type { MappedColumn, MappedData } from './dataUtils'
import { aggregate } from './drilldown'
import type { VisualJsonData, VisualLayout } from './types'

/** Leaf operators. Names match the SQL-side criteria operators. */
export type FilterOperator =
  | '=' | '!=' | 'in' | 'not in'
  | '>' | '>=' | '<' | '<=' | 'between'
  | 'contains' | 'not contains' | 'starts with' | 'ends with'
  | 'is null' | 'is not null'

/** One leaf test on one column. `values` holds 0..n operands. */
export interface FilterCondition {
  /** Column NAME as it appears in the visual's data (matched case-insensitively). */
  column: string
  operator: FilterOperator
  values?: unknown[]
}

/** A group of conditions / nested groups combined with AND or OR. */
export interface FilterExpression {
  condition: 'and' | 'or'
  rules: Array<FilterCondition | FilterExpression>
}

export type FilterRule = FilterCondition | FilterExpression

/** Why a column referenced by an expression could not be evaluated. */
export const FILTER_REASON_NOT_IN_DATA = 'not_in_data'
// The tile holds only the first MAX_VISUAL_ROWS rows of its data (its own query
// was capped). Filtering that arbitrary slice would report a partial total as if
// it were the answer, so the filter is declined outright rather than applied to
// part of the data.
export const FILTER_REASON_TRUNCATED = 'truncated'
export const FILTER_REASON_NO_ROWS = 'no_rows'

/** What a visual did with the filter it was given. */
export interface FilterResult {
  /** Column NAMEs from the expression that this visual could evaluate. */
  applied: string[]
  /** Columns it could not, with a machine reason (rendered by the host). */
  skipped: Array<{ column: string; reason: string }>
  rowsBefore: number
  rowsAfter: number
  /** True when measures were re-aggregated after filtering (see below). */
  regrouped: boolean
}

export interface FilterApplication extends FilterResult {
  /** The dataset to render — the original object when nothing was filtered. */
  jsonData: VisualJsonData
}

export function isFilterGroup(rule: FilterRule): rule is FilterExpression {
  return Boolean(rule) && Array.isArray((rule as FilterExpression).rules)
    && typeof (rule as FilterExpression).condition === 'string'
}

/** True when the expression holds at least one leaf condition. */
export function hasFilterRules(expr: FilterExpression | null | undefined): boolean {
  if (!expr || !Array.isArray(expr.rules)) return false
  return expr.rules.some((r) => (isFilterGroup(r) ? hasFilterRules(r) : Boolean(r && r.column)))
}

/** Every column NAME the expression references (de-duplicated, in order). */
export function filterColumns(expr: FilterExpression | null | undefined): string[] {
  const out: string[] = []
  const seen = new Set<string>()
  const walk = (e: FilterExpression) => {
    for (const rule of e.rules || []) {
      if (isFilterGroup(rule)) { walk(rule); continue }
      const col = String(rule?.column || '')
      if (col && !seen.has(col)) { seen.add(col); out.push(col) }
    }
  }
  if (expr) walk(expr)
  return out
}

/**
 * AND-combine two expressions (either may be null/empty).
 *
 * Used to fold a host-supplied filter (e.g. the dashboard's quick-filter chips)
 * together with the one the user set inside the visual, so both narrow the data
 * rather than one replacing the other. Each side keeps its own and/or semantics
 * by staying a nested group.
 */
export function combineFilters(
  ...exprs: Array<FilterExpression | null | undefined>
): FilterExpression | null {
  const usable = exprs.filter((e): e is FilterExpression => hasFilterRules(e))
  if (!usable.length) return null
  if (usable.length === 1) return usable[0]
  return { condition: 'and', rules: usable }
}

/** Stable key for an expression — hosts/memos use it as a dependency. */
export function filterKey(expr: FilterExpression | null | undefined): string {
  return hasFilterRules(expr) ? JSON.stringify(expr) : ''
}

// SQL compares CHAR(n) with PAD SPACE semantics: trailing blanks are not
// significant, so 'WON       ' = 'WON' in both Postgres (bpchar) and Oracle.
// JS string equality has no such rule and no collation option provides one, so
// the shared evaluator implements it — otherwise a criterion authored as 'WON'
// silently matches nothing against a CHAR(10) column, and the visual empties.
// (Same padding class executor._bl_fn_payload right-trims for BL functions.)
function padSpace(value: unknown): string {
  return String(value ?? '').replace(/ +$/, '')
}

// Raw-value equality with a string fallback: a numeric 10 from one renderer and
// the string '10' from another are the same value.
function eqValue(a: unknown, b: unknown): boolean {
  if (a === b) return true
  return padSpace(a) === padSpace(b)
}

function num(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

// Ordered comparison: numeric when both sides are numeric, date when both parse
// as dates, string otherwise. Returns null when the two aren't comparable.
function compare(a: unknown, b: unknown): number | null {
  const na = num(a)
  const nb = num(b)
  if (na !== null && nb !== null) return na === nb ? 0 : na < nb ? -1 : 1
  const da = Date.parse(String(a ?? ''))
  const db = Date.parse(String(b ?? ''))
  if (!Number.isNaN(da) && !Number.isNaN(db)) return da === db ? 0 : da < db ? -1 : 1
  const sa = padSpace(a)
  const sb = padSpace(b)
  return sa === sb ? 0 : sa < sb ? -1 : 1
}

/** Evaluate ONE leaf condition against a row value. */
export function evaluateCondition(value: unknown, rule: FilterCondition): boolean {
  const values = rule.values ?? []
  const first = values[0]
  const text = padSpace(value).toLowerCase()
  switch (rule.operator) {
    case '=':
      return eqValue(value, first)
    case '!=':
      return !eqValue(value, first)
    case 'in':
      return values.some((v) => eqValue(value, v))
    case 'not in':
      return !values.some((v) => eqValue(value, v))
    case '>': {
      const c = compare(value, first)
      return c !== null && c > 0
    }
    case '>=': {
      const c = compare(value, first)
      return c !== null && c >= 0
    }
    case '<': {
      const c = compare(value, first)
      return c !== null && c < 0
    }
    case '<=': {
      const c = compare(value, first)
      return c !== null && c <= 0
    }
    case 'between': {
      const lo = compare(value, values[0])
      const hi = compare(value, values[1])
      return lo !== null && hi !== null && lo >= 0 && hi <= 0
    }
    case 'contains':
      return text.includes(padSpace(first).toLowerCase())
    case 'not contains':
      return !text.includes(padSpace(first).toLowerCase())
    case 'starts with':
      return text.startsWith(padSpace(first).toLowerCase())
    case 'ends with':
      return text.endsWith(padSpace(first).toLowerCase())
    case 'is null':
      return value === null || value === undefined || value === ''
    case 'is not null':
      return !(value === null || value === undefined || value === '')
    default:
      // An unknown operator must never silently drop rows — treat it as "no
      // opinion" (the column is still reported as applied by the caller).
      return true
  }
}

/**
 * Evaluate a whole expression against one row.
 *
 * `resolveKey` maps an expression column NAME to the row's actual key (data keys
 * can be case-folded by the database). A column the row does not have is
 * IGNORED — the row survives — because a visual that lacks the column is
 * reported as unfiltered rather than emptied.
 */
export function evaluateFilterExpression(
  row: Record<string, unknown>,
  expr: FilterExpression,
  resolveKey: (column: string) => string | undefined,
): boolean {
  const rules = expr.rules || []
  const results: boolean[] = []
  for (const rule of rules) {
    if (isFilterGroup(rule)) {
      if (!hasFilterRules(rule)) continue
      results.push(evaluateFilterExpression(row, rule, resolveKey))
      continue
    }
    if (!rule || !rule.column) continue
    const key = resolveKey(rule.column)
    if (!key) continue // not this visual's column — no opinion
    results.push(evaluateCondition(row[key], rule))
  }
  if (!results.length) return true
  return expr.condition === 'or' ? results.some(Boolean) : results.every(Boolean)
}

// The dimension columns a visual actually DISPLAYS: visible, non-calc, not in a
// values group. These are the grain of what is on screen.
function displayedDims(md: MappedData): MappedColumn[] {
  const valueNames = new Set(pickGroup(md, 'values', 'value').map((c) => c.name))
  const out: MappedColumn[] = []
  const seen = new Set<string>()
  for (const group of Object.values(md.groups)) {
    for (const c of group) {
      if (c.hidden || c.isCalc || valueNames.has(c.name)) continue
      if (seen.has(c.name)) continue
      seen.add(c.name)
      out.push(c)
    }
  }
  return out
}

const AGG_RE = /SUM|COUNT|AVG|AVERAGE|MIN|MAX|MEDIAN|STDDEV|VARIANCE/i
const SEP = String.fromCharCode(1)

/**
 * Apply a filter expression to a visual's loaded dataset.
 *
 *  1. Rows are kept when the expression evaluates true for them.
 *  2. If the expression filters on a column FINER than what the visual displays
 *     (a hidden or non-grouped column) and the visual has aggregated measures,
 *     the survivors are re-grouped on the displayed dimensions and the measures
 *     re-aggregated — otherwise a chart would draw several points per category.
 *     Row-level visuals (a detail table with no aggregate measures) are only
 *     filtered: collapsing their rows would destroy the detail they exist for.
 *  3. Columns the dataset does not have are reported in `skipped` and change
 *     nothing — a visual is never emptied by a filter it cannot evaluate.
 *  4. A TRUNCATED dataset is not filtered at all. The tile holds an arbitrary
 *     first N rows of its data, so filtering it would report a partial total as
 *     if it were the answer — worse than not filtering, because it looks right.
 */
export function applyFilterToData(
  jsonData: VisualJsonData | undefined,
  visualLayout: VisualLayout | undefined,
  expr: FilterExpression | null | undefined,
  truncated = false,
): FilterApplication {
  const rowsBefore = Math.max(0, (jsonData?.length ?? 1) - 1)
  const inert: FilterApplication = {
    jsonData: jsonData as VisualJsonData,
    applied: [], skipped: [],
    rowsBefore, rowsAfter: rowsBefore, regrouped: false,
  }
  if (!jsonData || !visualLayout || !hasFilterRules(expr)) return inert
  if (truncated) {
    // Its own query hit the row ceiling, so these rows are a slice, not the
    // population. Decline every column and let the tile say so.
    return {
      ...inert,
      skipped: filterColumns(expr).map((column) => ({
        column, reason: FILTER_REASON_TRUNCATED,
      })),
    }
  }

  const md = mapVisualData(jsonData, visualLayout)
  const byLower = new Map<string, string>()
  for (const key of Object.keys(md.schema)) {
    if (key !== 'id') byLower.set(key.toLowerCase(), key)
  }
  const resolveKey = (column: string) => byLower.get(String(column).toLowerCase())

  const applied: string[] = []
  const skipped: Array<{ column: string; reason: string }> = []
  for (const column of filterColumns(expr)) {
    if (resolveKey(column)) applied.push(column)
    else skipped.push({ column, reason: FILTER_REASON_NOT_IN_DATA })
  }
  if (!applied.length) return { ...inert, skipped }
  if (!md.rows.length) {
    // Nothing to filter — report it rather than implying the filter emptied it.
    return {
      ...inert,
      skipped: filterColumns(expr).map((column) => ({ column, reason: FILTER_REASON_NO_ROWS })),
    }
  }

  const filtered = md.rows.filter((row) => evaluateFilterExpression(row, expr!, resolveKey))

  const dims = displayedDims(md)
  const dimNames = new Set(dims.map((d) => d.name))
  const appliedKeys = applied.map((c) => resolveKey(c)!).filter(Boolean)
  const finerThanDisplay = appliedKeys.some((key) => !dimNames.has(key))
  const valueCols = pickGroup(md, 'values', 'value')
  const hasAggregates = valueCols.some((c) => AGG_RE.test(String(c.agg || '')))

  let outRows = filtered
  let regrouped = false
  if (finerThanDisplay && hasAggregates) {
    const groups = new Map<string, { key: Record<string, unknown>; rows: Array<Record<string, unknown>> }>()
    for (const row of filtered) {
      const gk = dims.map((d) => String(row[d.name] ?? '')).join(SEP)
      let g = groups.get(gk)
      if (!g) {
        const keyObj: Record<string, unknown> = {}
        for (const d of dims) keyObj[d.name] = row[d.name]
        // Carry the filtered column(s) through as constants so tooltips and the
        // underlying-data view still show what the rows are scoped to.
        for (const key of appliedKeys) {
          if (!(key in keyObj)) keyObj[key] = row[key]
        }
        g = { key: keyObj, rows: [] }
        groups.set(gk, g)
      }
      g.rows.push(row)
    }
    outRows = []
    for (const g of groups.values()) {
      const row: Record<string, unknown> = { ...g.key }
      for (const vc of valueCols) {
        row[vc.name] = aggregate(g.rows.map((r) => r[vc.name]), vc.agg)
      }
      outRows.push(row)
    }
    regrouped = true
  }

  const outJson = [
    md.schema,
    ...outRows.map((r, i) => ({ ...r, id: r.id ?? i + 1 })),
  ] as unknown as VisualJsonData

  return {
    jsonData: outJson,
    applied,
    skipped,
    rowsBefore,
    rowsAfter: outRows.length,
    regrouped,
  }
}

/**
 * One row per DISPLAYED category for a chart handed row-level data.
 *
 * A freehand-SQL visual runs its query as written, so a SELECT with no GROUP BY
 * returns every record — six rows for one site + customer. The vendored charts
 * draw one mark per row, so those six bars stacked on one spot and the chart
 * showed the tallest (500) where the app server's own widget shows the total
 * (1,610): it aggregates its rows by the placed dimensions with each value
 * column's FUNCTION. This does the same, so one visual answers the same on both.
 *
 * Only data that actually has several rows per displayed category changes. A
 * schema-mode visual's query is already grouped on exactly those columns (and
 * any grouped dataset is), so it comes back unchanged — the same object.
 *
 * The rows are RAW here, not partial aggregates, so COUNT counts them and AVG is
 * the true mean — unlike `aggregate`, which re-combines already-grouped partials.
 * A column that is neither a dimension nor a value survives only where every row
 * in the group agrees on it (tooltips keep it; it never becomes a wrong value).
 */
export function collapseToDisplayedGrain(
  jsonData: VisualJsonData | undefined,
  visualLayout: VisualLayout | undefined,
): VisualJsonData | undefined {
  if (!jsonData || !visualLayout || jsonData.length < 3) return jsonData
  const md = mapVisualData(jsonData, visualLayout)
  const valueCols = pickGroup(md, 'values', 'value')
  if (!valueCols.length) return jsonData
  // Layout NAMEs and data keys can differ in case (the layout says site_code,
  // the query's alias SITE_CODE); resolve each name to the key the rows use.
  const byLower = new Map<string, string>()
  for (const key of [...Object.keys(md.schema), ...Object.keys(md.rows[0] ?? {})]) {
    if (!byLower.has(key.toLowerCase())) byLower.set(key.toLowerCase(), key)
  }
  const keyOf = (name: string) => byLower.get(name.toLowerCase()) ?? name
  const dimKeys = displayedDims(md).map((d) => keyOf(d.name))
  const values = valueCols.map((c) => ({ key: keyOf(c.name), agg: c.agg }))

  const groups = new Map<string, Array<Record<string, unknown>>>()
  for (const row of md.rows) {
    const gk = dimKeys.map((k) => String(row[k] ?? '')).join(SEP)
    const g = groups.get(gk)
    if (g) g.push(row)
    else groups.set(gk, [row])
  }
  if (groups.size === md.rows.length) return jsonData

  const valueNames = new Set(values.map((v) => v.key))
  const outRows: Array<Record<string, unknown>> = []
  for (const rows of groups.values()) {
    const out: Record<string, unknown> = {}
    for (const key of Object.keys(rows[0])) {
      if (key === 'id' || valueNames.has(key)) continue
      const first = rows[0][key]
      if (rows.every((r) => r[key] === first)) out[key] = first
    }
    for (const v of values) out[v.key] = aggregateRaw(rows.map((r) => r[v.key]), v.agg)
    outRows.push(out)
  }
  return [
    md.schema,
    ...outRows.map((r, i) => ({ ...r, id: i + 1 })),
  ] as unknown as VisualJsonData
}

/** Aggregate RAW row values with a column's FUNCTION (SUM when it has none). */
function aggregateRaw(values: unknown[], agg?: string): unknown {
  const present = values.filter((v) => v != null && v !== '')
  const fn = String(agg || '').toUpperCase().replace(/[\s_]+/g, '')
  if (fn.includes('DISTINCT')) return new Set(present.map((v) => String(v))).size
  if (fn.includes('COUNT')) return present.length
  const nums = present.map(Number).filter((n) => !Number.isNaN(n))
  // A text value placed in Values: there is nothing to add up, so keep what
  // the rows say rather than inventing a number.
  if (!nums.length) return present.length ? present[0] : null
  if (fn.includes('MIN')) return Math.min(...nums)
  if (fn.includes('MAX')) return Math.max(...nums)
  if (fn.includes('AVG') || fn.includes('AVERAGE')) {
    return nums.reduce((a, b) => a + b, 0) / nums.length
  }
  return nums.reduce((a, b) => a + b, 0)
}
